/**
 * <s-fit-check> — step-by-step wizard that tells shoppers whether the headlight and tail light kits fit their Lexus.
 *
 * Rules (on the radio inputs):
 *   input[data-fit="range"] — fits when the chosen year is within data-min-year..data-max-year.
 *   input[data-fit="ask"]   — not certain; shopper is asked to get in touch.
 *   input[data-fit="no"]    — does not fit.
 *   input[data-front|data-rear="yes|ask|no"] on model/lighting/market — per-kit limits; the strictest result wins.
 *
 * Markup:
 *   form[data-fit-check-form] containing fieldset[data-step] question steps (radio groups model, year, lighting, market)
 *   and a final [data-step="result"] step.
 *   [data-fit-check-progress] with [data-progress-label] (data-template uses __current__) and [data-progress-segment] items.
 *   [data-fit-check-error], [data-fit-check-summary]
 *   [data-fit-check-kit] containing [data-state="yes|no|ask"] elements
 *   template[data-summary="yes|no|ask|mixed"] — summary copy for each result
 *   [data-show-for-market="us unknown"] — note shown only for the listed market values
 *   [data-step-focus] — heading focused when its step opens
 *
 * Commands (commandfor this element): --next, --back, --restart.
 * Picking a tile with a pointer advances automatically; keyboard users confirm with Next or Enter.
 */
class SFitCheck extends HTMLElement {
  #ac;
  #index = 0;
  #pointer = false;
  #advanceTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const form = this.querySelector('[data-fit-check-form]');

    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#next();
      if (event.command === '--back') this.#show(this.#index - 1);
      if (event.command === '--restart') {
        form?.reset();
        this.#show(0);
      }
    }, { signal });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#next();
    }, { signal });

    // Arrow keys change radio selection too, so only pointer picks advance on their own.
    this.addEventListener('pointerdown', () => { this.#pointer = true; }, { signal });
    this.addEventListener('keydown', () => { this.#pointer = false; }, { signal });
    form?.addEventListener('change', () => {
      this.#setError(false);
      if (!this.#pointer) return;
      clearTimeout(this.#advanceTimer);
      this.#advanceTimer = setTimeout(() => this.#next(), 260);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#advanceTimer);
  }

  get #steps() {
    return [...this.querySelectorAll('[data-step]')];
  }

  #next() {
    clearTimeout(this.#advanceTimer);
    const steps = this.#steps;
    const current = steps[this.#index];
    if (!current || current.dataset.step === 'result') return;
    if (!current.querySelector('input:checked')) {
      this.#setError(true);
      return;
    }
    const nextStep = steps[this.#index + 1];
    if (nextStep?.dataset.step === 'result') this.#check();
    this.#show(this.#index + 1);
  }

  #setError(visible) {
    const error = this.querySelector('[data-fit-check-error]');
    if (error) error.hidden = !visible;
  }

  #show(index) {
    const steps = this.#steps;
    if (index < 0 || index >= steps.length) return;
    this.#index = index;
    this.#setError(false);
    steps.forEach((step, i) => { step.hidden = i !== index; });

    const step = steps[index];
    const questionCount = steps.filter((s) => s.dataset.step !== 'result').length;
    const isResult = step.dataset.step === 'result';
    const progress = this.querySelector('[data-fit-check-progress]');
    if (progress) {
      progress.hidden = isResult;
      const label = progress.querySelector('[data-progress-label]');
      if (label) label.textContent = label.dataset.template.replace('__current__', String(index + 1));
      progress.querySelectorAll('[data-progress-segment]').forEach((segment, i) => {
        segment.toggleAttribute('data-active', i <= index);
      });
    }
    const nav = this.querySelector('[data-fit-check-nav]');
    if (nav) nav.hidden = isResult;
    const back = this.querySelector('[command="--back"]');
    if (back) back.hidden = index === 0;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion) {
      const ease = getComputedStyle(this).getPropertyValue('--ease-ui').trim() || 'ease-out';
      step.animate(
        [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }],
        { duration: 360, easing: ease },
      );
    }

    const focusTarget = step.querySelector('[data-step-focus]');
    focusTarget?.focus({ preventScroll: true });
    const top = this.getBoundingClientRect().top;
    if (top < 0) this.scrollIntoView({ behavior: reduceMotion ? 'instant' : 'smooth', block: 'start' });
    if (questionCount && isResult) this.dataset.complete = '';
    else delete this.dataset.complete;
  }

  #check() {
    const pick = (name) => this.querySelector(`input[name="${name}"]:checked`);
    const model = pick('model');
    const year = pick('year');
    const lighting = pick('lighting');
    const market = pick('market');

    // Model + year decide the base result; per-kit limits on model, lighting and market can only make it stricter.
    const min = Number(this.dataset.minYear);
    const max = Number(this.dataset.maxYear);
    let base = model?.dataset.fit || 'ask';
    if (base === 'range') base = Number(year?.value) >= min && Number(year?.value) <= max ? 'yes' : 'no';

    const states = {};
    this.querySelectorAll('[data-fit-check-kit]').forEach((kit) => {
      const key = kit.dataset.fitCheckKit;
      const state = worst([base, ...[model, lighting, market].map((input) => input?.dataset[key] || 'yes')]);
      states[key] = state;
      kit.dataset.result = state;
      kit.querySelectorAll('[data-state]').forEach((el) => {
        el.hidden = !el.dataset.state.split(' ').includes(state);
      });
    });

    // Market-specific notes, e.g. the US daytime running light coding tip.
    this.querySelectorAll('[data-show-for-market]').forEach((el) => {
      el.hidden = !el.dataset.showForMarket.split(' ').includes(market?.value);
    });

    const unique = [...new Set(Object.values(states))];
    const summaryKey = unique.length === 1 ? unique[0] : 'mixed';
    const summary = this.querySelector('[data-fit-check-summary]');
    const template = this.querySelector(`template[data-summary="${summaryKey}"]`);
    if (summary && template) summary.textContent = template.content.textContent.trim();
  }
}

const RANK = { yes: 0, ask: 1, no: 2 };
function worst(states) {
  return states.reduce((a, b) => ((RANK[b] ?? 1) > (RANK[a] ?? 1) ? b : a), 'yes');
}

if (!customElements.get('s-fit-check')) {
  customElements.define('s-fit-check', SFitCheck);
}
